/**
 * Financials Screen - MOBILE ONLY
 * 
 * Mortgage calculator and financial analysis tools for opportunity evaluation.
 * Includes loan calculations, monthly payments, and investment metrics.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { View, Text, ScrollView, TextInput, Pressable } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, space, icon } from '../../../shared/tokens/mobile';
import { ScreenContainer, ScreenHeader } from '../../../components/v2/mobile';
import { formatCurrency, formatCurrencyExact } from '../../../shared/utils/formatters';
import { financialsStyles as styles } from './styles/financialsStyles';

export default function FinancialsScreen() {
  const router = useRouter();
  const { opportunityName, askingPrice: initialPrice, acreage: initialAcreage } = useLocalSearchParams<{
    opportunityName?: string;
    askingPrice?: string;
    acreage?: string;
  }>();

  const handleBack = useCallback(() => router.back(), [router]);

  // Calculator inputs
  const [purchasePrice, setPurchasePrice] = useState(initialPrice || '500000');
  const [downPaymentPercent, setDownPaymentPercent] = useState('20');
  const [interestRate, setInterestRate] = useState('7.0');
  const [loanTermYears, setLoanTermYears] = useState('30');
  const [customTerm, setCustomTerm] = useState('');
  const [propertyTaxRate, setPropertyTaxRate] = useState('1.0');
  const [insuranceAnnual, setInsuranceAnnual] = useState('1500');
  const acreage = parseFloat(initialAcreage || '0') || 0;

  // Parse values
  const price = parseFloat(purchasePrice.replace(/,/g, '')) || 0;
  const downPercent = parseFloat(downPaymentPercent) || 0;
  const rate = parseFloat(interestRate) || 0;
  const term = parseInt(loanTermYears) || 30;
  const taxRate = parseFloat(propertyTaxRate) || 0;
  const insurance = parseFloat(insuranceAnnual) || 0;

  // Calculate mortgage
  const calculations = useMemo(() => {
    const downPayment = price * (downPercent / 100);
    const loanAmount = price - downPayment;
    const monthlyRate = rate / 100 / 12;
    const numPayments = term * 12;

    // Monthly P&I payment (M = P[r(1+r)^n]/[(1+r)^n-1])
    let monthlyPI = 0;
    if (monthlyRate > 0 && numPayments > 0) {
      monthlyPI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                  (Math.pow(1 + monthlyRate, numPayments) - 1);
    } else if (numPayments > 0) {
      monthlyPI = loanAmount / numPayments;
    }

    const monthlyTax = (price * (taxRate / 100)) / 12;
    const monthlyInsurance = insurance / 12;
    const totalMonthly = monthlyPI + monthlyTax + monthlyInsurance;
    const totalInterest = (monthlyPI * numPayments) - loanAmount;
    const totalCost = price + totalInterest + (monthlyTax * numPayments) + (monthlyInsurance * numPayments);

    return {
      downPayment,
      loanAmount,
      monthlyPI: isNaN(monthlyPI) ? 0 : monthlyPI,
      monthlyTax,
      monthlyInsurance,
      totalMonthly: isNaN(totalMonthly) ? 0 : totalMonthly,
      totalInterest: isNaN(totalInterest) ? 0 : totalInterest,
      totalCost: isNaN(totalCost) ? 0 : totalCost,
    };
  }, [price, downPercent, rate, term, taxRate, insurance]);

  return (
    <ScreenContainer>
      <ScreenHeader title={opportunityName || 'Financials'} onBack={handleBack} />

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {/* RESULTS CARD */}
        <View style={styles.resultsCard}>
          <Text style={styles.resultsTitle}>Monthly Payment</Text>
          <Text style={styles.resultsAmount}>{formatCurrencyExact(calculations.totalMonthly, 2)}</Text>
          
          <View style={styles.resultsBreakdown}>
            <ResultRow label="Principal & Interest" value={calculations.monthlyPI} />
            <ResultRow label="Property Tax" value={calculations.monthlyTax} />
            <ResultRow label="Insurance" value={calculations.monthlyInsurance} />
          </View>
        </View>

        {/* LOAN SUMMARY */}
        <View style={styles.summaryCard}>
          <Text style={styles.sectionTitle}>Loan Summary</Text>
          <SummaryRow label="Down Payment" value={formatCurrency(calculations.downPayment)} />
          <SummaryRow label="Loan Amount" value={formatCurrency(calculations.loanAmount)} />
          <SummaryRow label="Total Interest" value={formatCurrency(calculations.totalInterest)} highlight />
          <SummaryRow label="Total Cost" value={formatCurrency(calculations.totalCost)} />
        </View>

        {/* INPUTS */}
        <View style={styles.inputsCard}>
          <Text style={styles.sectionTitle}>Calculator Inputs</Text>

          <InputField
            label="Purchase Price"
            value={purchasePrice}
            onChangeText={setPurchasePrice}
            prefix="$"
            keyboardType="numeric"
          />

          <InputField
            label="Down Payment"
            value={downPaymentPercent}
            onChangeText={setDownPaymentPercent}
            suffix="%"
            keyboardType="numeric"
          />

          <InputField
            label="Interest Rate"
            value={interestRate}
            onChangeText={setInterestRate}
            suffix="%"
            keyboardType="numeric"
          />

          <View style={styles.termRow}>
            <Text style={styles.inputLabel}>Loan Term</Text>
            <View style={styles.termButtons}>
              {[15, 20, 30].map(years => (
                <Pressable
                  key={years}
                  style={[styles.termButton, loanTermYears === String(years) && !customTerm && styles.termButtonActive]}
                  onPress={() => { setLoanTermYears(String(years)); setCustomTerm(''); }}
                >
                  <Text style={[styles.termButtonText, loanTermYears === String(years) && !customTerm && styles.termButtonTextActive]}>
                    {years} yr
                  </Text>
                </Pressable>
              ))}
            </View>
            <View style={styles.customTermRow}>
              <Text style={styles.customTermLabel}>Custom:</Text>
              <TextInput
                style={styles.customTermInput}
                value={customTerm}
                onChangeText={(val) => {
                  const filtered = val.replace(/[^0-9]/g, '');
                  setCustomTerm(filtered);
                  if (filtered) setLoanTermYears(filtered);
                }}
                placeholder="years"
                placeholderTextColor={color.textMuted}
                keyboardType="numeric"
                inputMode="numeric"
              />
            </View>
          </View>

          <InputField
            label="Property Tax Rate"
            value={propertyTaxRate}
            onChangeText={setPropertyTaxRate}
            suffix="% / year"
            keyboardType="numeric"
          />

          <InputField
            label="Annual Insurance"
            value={insuranceAnnual}
            onChangeText={setInsuranceAnnual}
            prefix="$"
            keyboardType="numeric"
          />
        </View>

        {/* QUICK METRICS */}
        <View style={styles.metricsCard}>
          <Text style={styles.sectionTitle}>Quick Metrics</Text>
          <View style={styles.metricsGrid}>
            <MetricBox 
              label="Price/Acre" 
              value={acreage > 0 && price > 0 ? formatCurrency(price / acreage) : '—'} 
              icon="resize-outline"
              hint={acreage > 0 ? `${acreage.toFixed(1)} acres` : 'Add acreage'}
            />
            <MetricBox 
              label="Down/Acre" 
              value={acreage > 0 && calculations.downPayment > 0 ? formatCurrency(calculations.downPayment / acreage) : '—'} 
              icon="wallet-outline"
              hint={acreage > 0 ? 'Down per acre' : 'Add acreage'}
            />
            <MetricBox 
              label="Monthly/Acre" 
              value={acreage > 0 && calculations.totalMonthly > 0 ? formatCurrencyExact(calculations.totalMonthly / acreage, 0) : '—'} 
              icon="calendar-outline"
              hint={acreage > 0 ? 'Monthly per acre' : 'Add acreage'}
            />
            <MetricBox 
              label="Total Interest" 
              value={calculations.totalInterest > 0 ? formatCurrency(calculations.totalInterest) : '—'} 
              icon="trending-up-outline"
              hint={`Over ${term} years`}
            />
          </View>
        </View>

        <View style={{ height: space.xxl }} />
      </ScrollView>
    </ScreenContainer>
  );
}

// Result row component - uses exact formatting (no rounding)
function ResultRow({ label, value }: { label: string; value: number }) {
  return (
    <View style={styles.resultRow}>
      <Text style={styles.resultLabel}>{label}</Text>
      <Text style={styles.resultValue}>{formatCurrencyExact(value, 2)}</Text>
    </View>
  );
}

// Summary row component  
function SummaryRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <View style={styles.summaryRow}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={[styles.summaryValue, highlight && styles.summaryValueHighlight]}>{value}</Text>
    </View>
  );
}

// Input field component
interface InputFieldProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  prefix?: string;
  suffix?: string;
  keyboardType?: 'default' | 'numeric';
}

function InputField({ label, value, onChangeText, prefix, suffix, keyboardType }: InputFieldProps) {
  // Handle text change with numeric filtering for numeric inputs
  const handleChange = useCallback((newValue: string) => {
    if (keyboardType === 'numeric') {
      // Allow only numbers and decimal point
      const filtered = newValue.replace(/[^0-9.]/g, '');
      onChangeText(filtered);
    } else {
      onChangeText(newValue);
    }
  }, [keyboardType, onChangeText]);

  return (
    <View style={styles.inputGroup}>
      <Text style={styles.inputLabel}>{label}</Text>
      <View style={styles.inputWrapper}>
        {prefix && <Text style={styles.inputPrefix}>{prefix}</Text>}
        <TextInput
          style={styles.input}
          value={value}
          onChangeText={handleChange}
          keyboardType={keyboardType}
          placeholderTextColor={color.textMuted}
          inputMode={keyboardType === 'numeric' ? 'decimal' : 'text'}
        />
        {suffix && <Text style={styles.inputSuffix}>{suffix}</Text>}
      </View>
    </View>
  );
}

// Metric box component
function MetricBox({ label, value, icon: iconName, hint }: { label: string; value: string; icon: string; hint?: string }) {
  return (
    <View style={styles.metricBox}>
      <Ionicons name={iconName as any} size={icon.md} color={color.textMuted} />
      <Text style={styles.metricValue}>{value}</Text>
      <Text style={styles.metricLabel}>{label}</Text>
      {hint && <Text style={styles.metricHint}>{hint}</Text>}
    </View>
  );
}

// Styles extracted to ./styles/financialsStyles.ts per cursor rules

